import * as React from 'react';
import actions from '../actions';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Grid2 as Grid,
} from '@mui/material';
import {
    BaseElement,
    BaseSettingsContainer,
    Button,
    HeaderButton,
    NumberField,
    TextField,
    WarningDialog,
} from '../components';
import { ContainerContext, mapProps } from './index';
import { connect } from 'react-redux';
import { getTitle } from '../actions/title';
import { UserRole, UserTitleRole } from '../enums';
import { Delete, SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import { styled } from '@mui/styles';
import { useNavigate } from 'react-router';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props extends ContainerContext.Props {
    readonly title?: Entities.Title | null;
    readonly config?: Entities.Config;
}

type DialogType = 'Delete';
interface State {
    readonly openDialog: DialogType | null;
    readonly name: string;
    readonly apiUrl: string;
    readonly apiSecretKey: string;
    readonly analyticsEnable: boolean;
    readonly file: File | null;
}

class Container extends BaseElement<Props, State> {
    state = this.initState();

    async componentDidMount() {
        getTitle();
        await actions.title.getConfig();
    }

    protected renderContainer(): React.JSX.Element {
        const buttons: HeaderButton[] | undefined = this.props.userTitleRole === UserTitleRole.Viewer ? undefined : [
            { text: 'Delete Title', icon: Delete, color: 'error', onClick: this.openDeleteDialog},
        ];
        return (
            <BaseSettingsContainer
                {...this.props}
                styled={this.props.styled}
                title = "Project settings"
                TitleIcon = {SettingsIcon}
                buttons = {buttons}
            >
                {this.renderContent()}
            </BaseSettingsContainer>
        );
    }

    protected renderDialogs(): React.JSX.Element {
        return (
            <>
                {this.renderDeleteDialog()}
            </>
        );
    }

    private renderContent = () => {
        const { config, title, userRole } = this.props;
        if (!config || !title) {
            return;
        }

        const { name, apiUrl, apiSecretKey, analyticsEnable } = this.state;
        const myslqReplications: React.JSX.Element[] = [];
        if (config.mysqlReplication && config.mysqlReplication.slaves) {
            config.mysqlReplication.slaves.forEach((slave) => {
                myslqReplications.push(
                    <>
                        <Grid size={12}>
                            <TextField
                                label="Slave Host"
                                fullWidth={true}
                                readOnly={true}
                                value={slave.host}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Slave Database"
                                fullWidth={true}
                                readOnly={true}
                                value={slave.database}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Slave Port"
                                fullWidth={true}
                                readOnly={true}
                                value={slave.port}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Slave Username"
                                fullWidth={true}
                                readOnly={true}
                                value={slave.username}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Slave Password"
                                fullWidth={true}
                                readOnly={true}
                                value={slave.password}
                                variant="outlined"
                            />
                        </Grid>
                    </>
                );
            });
        }

        return (
            <Grid container={true} spacing={2}>
                {userRole === UserRole.Admin && (
                    <Grid size={12}>
                        <Card key={'Settings'} >
                            <StyledCardHeader title={'Settings'}/>
                            <CardContent>
                                <Grid container={true} spacing={0}>
                                    <Grid size={12}>
                                        <TextField
                                            label="Name"
                                            fullWidth={true}
                                            value={name}
                                            required={true}
                                            variant="outlined"
                                            onChange={this.updateName}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Api Url"
                                            fullWidth={true}
                                            value={apiUrl}
                                            required={true}
                                            variant="outlined"
                                            onChange={this.updateApiUrl}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Api Secret Key"
                                            fullWidth={true}
                                            value={apiSecretKey}
                                            required={true}
                                            variant="outlined"
                                            onChange={this.updateApiSecretKey}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <CardActions>
                                <Button text="Update" onClick={this.onUpdate} variant="outlined" disabled={!this.updateEnable()} />
                            </CardActions>
                        </Card>
                    </Grid>
                )}
                <Grid size={12}>
                    <Card key={'general'} >
                        <StyledCardHeader title={'General'} />
                        <CardContent>
                            <Grid container={true} spacing={0}>
                                <Grid size={12}>
                                    <TextField
                                        label="App Name"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.appName}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Release Version"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.releaseVersion}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Session Duration"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={`${config.sessionDurationSec} seconds`}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Secret Key"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.secretKey}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Terms Of Service Url"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.termsOfServiceUrl}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Privacy Policy Url"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.privacyPolicyUrl}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                {(config.mysqlReplication && !config.mysqlReplication.enabled) && config.mysql && (
                    <Grid size={12}>
                        <Card key={'mysql'} >
                            <StyledCardHeader title={'Msql'} />
                            <CardContent>
                                <Grid container={true} spacing={0}>
                                    <Grid size={12}>
                                        <TextField
                                            label="Host"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysql.host}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Database"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysql.database}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <NumberField
                                            label="Port"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysql.port}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Username"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysql.username}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Password"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysql.password}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Connection Limit"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysql.extra.connectionLimit}
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {config.mysqlReplication && config.mysqlReplication.enabled && (
                    <Grid size={12}>
                        <Card key={'mysql'} >
                            <StyledCardHeader title={'Mysql Replication'} />
                            <CardContent>
                                <Grid container={true} spacing={0}>
                                    <Grid size={12}>
                                        <TextField
                                            label="Master Host"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysqlReplication.master.host}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Master Database"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysqlReplication.master.database}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Master Port"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysqlReplication.master.port}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Master Username"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysqlReplication.master.username}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            label="Master Password"
                                            fullWidth={true}
                                            readOnly={true}
                                            value={config.mysqlReplication.master.password}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    {myslqReplications}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                <Grid size={12}>
                    <Card key={'redis'} >
                        <StyledCardHeader title={'Redis'} />
                        <CardContent>
                            <Grid container={true} spacing={0}>
                                <Grid size={12}>
                                    <TextField
                                        label="Host"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.redis.host}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <NumberField
                                        label="Port"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.redis.port}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={12}>
                    <Card key={'google_configuration'} >
                        <StyledCardHeader title={'Google'} />
                        <CardContent>
                            <Grid container={true} spacing={0}>
                                <Grid size={12}>
                                    <TextField
                                        label="Package Id"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.google.packageId}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Licence Key"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.google.licenceKey}
                                        multiline={true}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Play Games Client Id"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.google.playGamesClientId}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        label="Play Games Client Secret"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.google.playGamesClientSecret}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={12}>
                    <Card key={'apple_configuration'} >
                        <StyledCardHeader title={'Apple'} />
                        <CardContent>
                            <Grid container={true} spacing={0}>
                                <Grid size={12}>
                                    <TextField
                                        label="Bundle Id"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.apple.bundleId}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={12}>
                    <Card key={'facebook_configuration'} >
                        <StyledCardHeader title={'Facebook'} />
                        <CardContent>
                            <Grid container={true} spacing={0}>
                                <Grid size={12}>
                                    <TextField
                                        label="App Token"
                                        fullWidth={true}
                                        readOnly={true}
                                        value={config.facebook.appToken}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    }

    private initState(): State {
        const { title } = this.props;
        if (!title) {
            return {
                openDialog: null,
                name: '',
                file: null,
                apiUrl: '',
                apiSecretKey: '',
                analyticsEnable: false,
            };
        }
        const { name, apiUrl, apiSecretKey, analyticsEnable } = title;
        return {
            openDialog: null,
            name,
            file: null,
            apiUrl,
            apiSecretKey,
            analyticsEnable,
        };
    }

    private updateName = (name: string) => this.setState({ name });

    private updateApiUrl = (apiUrl: string) => this.setState({ apiUrl });

    private updateApiSecretKey = (apiSecretKey: string) => this.setState({ apiSecretKey });

    private updateAnalyticsEnable= (analyticsEnable: boolean) => this.setState({ analyticsEnable });

    private updateEnable = (): boolean =>  {
        const { title } = this.props;
        const { name, apiUrl, apiSecretKey, file, analyticsEnable } = this.state;
        if (!title) {
            return false;
        }
        return title.name !== name ||
            title.apiUrl !== apiUrl ||
            title.apiSecretKey !== apiSecretKey ||
            file !== null ||
            title.analyticsEnable !== analyticsEnable;

    }

    private handleDrop = (file: File) => this.setState({file});

    private onUpdate = async () => {
        const { title } = this.props;
        const { name, apiUrl, apiSecretKey, analyticsEnable, file } = this.state;
        if (!title) {
            return;
        }

        await actions.title.update(title.id, name, apiUrl, apiSecretKey, analyticsEnable, file);
    };

    private openDeleteDialog = () => this.setState({ openDialog: 'Delete' });
    private renderDeleteDialog = () => {
        const { openDialog } = this.state;
        const onDelete = async () => {
            const { title } = this.props;
            if (!title) {
                return;
            }

            await actions.title.deleteTitle(title.id);
            this.toLink('/titles');
        };

        return (
            <WarningDialog
                styled={this.props.styled}
                open={openDialog === 'Delete'}
                title="Delete Title"
                content="Delete title and all associated data? This cannot be undone."
                onClose={this.closeDialog}
                onSubmit={onDelete}
                maxWidth={'xs'}
            />
        );
    };

    private closeDialog = () => this.setState({ openDialog: null });
}

const StyledCardHeader = styled(CardHeader)(({
    '&.MuiCardHeader-root': {
        height: 0,
        padding: '12px 5px',
    },
    '& .MuiCardHeader-title': {
        fontWeight: 600,
        fontSize: 12,
    },
}));

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
    title: state.title.selectedTitle,
    config: state.title.config,
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
